// user-search.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import MiniSearch, { SearchResult } from 'minisearch';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { SubSink } from 'subsink';

import { PotentialCrewMember } from './store/dispatch.reducer';
import { selectCrewForLocalSearch } from './store/dispatch.selectors';

export type UserMiniSearchStoreFields = keyof PotentialCrewMember;

@Injectable({
    providedIn: 'root',
})
export class DispatchUserSearchService implements OnDestroy {
    private subs = new SubSink();
    public localCrew$: Observable<PotentialCrewMember[]>;

    private userMiniSearch = new MiniSearch({
        fields: ['name'] as UserMiniSearchStoreFields[],
        storeFields: [
            'name',
            'eventsCount',
            'revenue',
            'role',
            'user',
            'isDriver',
            'isAssigned',
            'isUnavailable',
            'isBookedOff',
            'estWorkingTime',
            'hoursWorked',
            'overtimeHours',
        ] as UserMiniSearchStoreFields[],
        searchOptions: {
            prefix: true,
            fuzzy: true,
        },
    });

    constructor(private store: Store) {
        this.initializeMiniSearch();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    private initializeMiniSearch() {
        this.localCrew$ = this.store.select(selectCrewForLocalSearch);

        this.subs.sink = this.localCrew$.subscribe((users: PotentialCrewMember[]) => {
            if (users.length === 0) {
                return;
            }
            this.userMiniSearch.removeAll();
            this.userMiniSearch.addAll(
                users.map((user) => ({ ...user, id: user.user.id })),
            );
        });
    }

    public searchUsers(
        searchTerm$: Observable<string>,
        potentialCrew$: Observable<PotentialCrewMember[]>,
    ): Observable<PotentialCrewMember[] | SearchResult[]> {
        return searchTerm$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((searchTerm) => {
                if (!searchTerm.trim()) {
                    return potentialCrew$;
                }
                return of(this.userMiniSearch.search(searchTerm));
            }),
        );
    }
}
